/* import __COLOCATED_TEMPLATE__ from './lsg-decimal-input.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import clsx from 'clsx';

const CLASS_NAME = 'LSG-c-decimal-input';
const NUMBER_INPUT_CLASS_NAME = `${CLASS_NAME}__number`;
const NUMBER_INPUT_SELECTED_CLASS_NAME = `${NUMBER_INPUT_CLASS_NAME}--selected`;

/**
 * Potential background colors for selected values
 *
 * @type {Object}
 */
 export const LSG_DECIMAL_INPUT__SELECTION_BACKGROUND_COLOR = {
  DEFAULT: 'yellow',
  GREEN: 'green'
};

/**
 * @type {Object} File extensions keyed by file category.
 */
 export const INPUT_FIELDS = {
  WHOLE: 0,
  DECIMAL: 1,
};

const range = (start, stop, step) => Array.from({ length: (stop - start) / step + 1}, (_, i) => start + (i * step));

class LsgDecimalInputComponent extends Component {
  @tracked editMode = false;

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get classNameSelectedInput() {
    let backgroundClass = '';
    switch(this.args.selectionBackgroundColor) {
      case LSG_DECIMAL_INPUT__SELECTION_BACKGROUND_COLOR.GREEN:
        backgroundClass = 'lsg-selection-bg-green';
        break;
    }

    return clsx(
      NUMBER_INPUT_CLASS_NAME,
      NUMBER_INPUT_SELECTED_CLASS_NAME,
      backgroundClass,
    );
  }

  get inputFieldWhole() {
    return INPUT_FIELDS.WHOLE;
  }

  get inputFieldDecimal() {
    return INPUT_FIELDS.DECIMAL;
  }

  /**
   * Minimum whole number
   */
  get wholeNumberMin() {
    return this.args.wholeNumberStart ?? 1;
  }

  /**
   * Minimum decimal number
   */
   get decimalNumberMin() {
    return this.args.decimalNumberStart ?? 0;
  }

  /**
   * Maximum whole number
   */
  get wholeNumberMax() {
    return this.args.wholeNumberEnd ?? 5;
  }

  /**
   * Maximum decimal number
   */
   get decimalNumberMax() {
    return this.args.decimalNumberEnd ?? 9;
  }

  /**
   * How many digits to increment by
   */
  get wholeNumberStep() {
    return this.args.wholeNumberStep ?? 1;
  }

  /**
   * How many decimal places to increment by
   */
   get decimalNumberStep() {
    return this.args.decimalNumberStep ?? 1;
  }

  get rangeWholeNumbers() {
    return range(
      this.wholeNumberMin,
      this.wholeNumberMax,
      this.wholeNumberStep,
    );
  }
  
  get rangeDecimalNumbers() {
    return range(
      this.decimalNumberMin,
      this.decimalNumberMax,
      this.decimalNumberStep,
    );
  }

  /**
   * Whole number editor will be open by default.
   */
  get isEditingWholeNumber() {
    return this.editMode === INPUT_FIELDS.WHOLE;
  }
  
  get isEditingDecimalNumber() {
    return this.editMode === INPUT_FIELDS.DECIMAL;
  }

  get isUnrated() {
    return !this.args.wholeNumberValue && !this.args.decimalValue;
  }

  get isInputSelectionAllowed() {
    return this.isUnrated || this.isEditingWholeNumber || this.isEditingDecimalNumber;
  }

  /**
   * Decimal editor will be closed by default.
   * Will not be shown if selected whole number is the max value.
   */
  get shouldDisplayDecimal() {
    return !this.isEditingWholeNumber
      && this.args.wholeNumberValue !== undefined
      && this.args.wholeNumberValue < this.wholeNumberMax;
  }

  /**
   * Click handler to open editor
   * 
   * @param {string} inputName one of type INPUT_FIELDS
   */
  @action
  onOpenEditor(inputName) {
    this.editMode = inputName;
    this.args.onChange(inputName, undefined);
  }

  /**
   * Click handler to close editor and update value.
   * Calls the onChange action to update the value
   * 
   * @param {string} inputName one of type INPUT_FIELDS
   * @param {number} newValue 
   */
   @action
   onCloseEditor(inputName, newValue) {
    this.editMode = false;
    this.args.onChange(inputName, newValue);
   }
}

export default LsgDecimalInputComponent;
